import { Button } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import {
  ChoiceSelect,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/design';
import type { IExportPreset, IListQuery, ListExportFormat } from '../../api/lists-contracts';
import { listsApi } from '../../api/lists-api';
import { teamFirmsApi } from '../../api/team-firms-api';
import { useAdminData } from '../../components/admin-page';
import {
  ExportBar,
  ExportLine,
  cancelExport,
  dismissExport,
  letGoExport,
  startExport,
  useExport
} from './export-downloads';

/**
 * The export dialog (SR-230, design `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md`
 * §3.11) — one for Lists and Reports: the format, *Excel (.xlsx)* or *CSV*; the columns **as shown**
 * or **a layout** — the survey's export layouts and those for any survey, each with its firm; every
 * row the list matches; *Download*. `POST /lists/export` through `apiDownload`, the file named by
 * survey-api's `Content-Disposition` where the browser is shown it, else by the survey and format.
 *
 * **The file streams under the dialog's eyes** (SR-418, `export-downloads.tsx`): the rows it holds,
 * the megabytes received and the time, under a moving bar, the choices held. *Continue in
 * background* — or the dialog closed any other way — hands it to the Team Portal's panel and lets
 * the reader work on; *Cancel* stops it. Saved, the dialog closes as before.
 *
 * **The query is the screen's**, the view's columns as shown — never the read's extra columns (the
 * name, a grouping's or a rule's field), which are the grid's and not the list's. **Internal ID is
 * always written first**, whatever the layout (survey-api's `ListExportColumns`), and the dialog
 * says so. A layout's firm is named by the team firm register where it can be read, else by its code.
 *
 * **survey-api judges every part**: a 400 at `presetId` ("is for another survey"), at `format` or
 * at `query.…` is said in the dialog in its words; a refusal is never a 200 with an error inside (its
 * rule), and a download that fails mid-file is said where the export is drawn by then.
 */

const FORMATS: readonly { format: ListExportFormat; label: string; extension: string }[] = [
  { format: 'xlsx', label: 'Excel (.xlsx)', extension: 'xlsx' },
  { format: 'csv', label: 'CSV', extension: 'csv' }
];

export function ExportDialog({
  open,
  onClose,
  survey,
  query,
  shownColumns,
  total
}: {
  open: boolean;
  onClose: () => void;
  survey: string;
  /** The screen's query, its columns as shown. */
  query: IListQuery;
  shownColumns: number;
  /** Every row the query matches — the read's `total`, null while it is out. */
  total: Nullable<number>;
}) {
  const [format, setFormat] = React.useState<ListExportFormat>('xlsx');
  const [presetId, setPresetId] = React.useState<Nullable<string>>(null);
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  const [exportId, setExportId] = React.useState<Nullable<number>>(null);
  const running = useExport(exportId);
  const [wasOpen, setWasOpen] = React.useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setSaid(null);
  }
  const presets = useAdminData(
    (signal) => (open ? listsApi.presets({ survey }, signal) : Promise.resolve(null)),
    [open, survey]
  );
  const firms = useAdminData(
    (signal) => (open ? teamFirmsApi.list(signal) : Promise.resolve(null)),
    [open]
  );
  const firmLabel = (code: string) => firms.data?.find((firm) => firm.code === code)?.label ?? code;
  const layouts = presets.data ?? [];
  const chosen = layouts.find((preset) => preset.id === presetId) ?? null;

  const download = () => {
    setSaid(null);
    const extension = FORMATS.find((one) => one.format === format)?.extension ?? format;
    setExportId(
      startExport(
        { query, format, presetId: chosen?.id ?? null },
        `${survey.toLowerCase()}-list.${extension}`
      )
    );
  };

  // Settled under the dialog: saved closes it, a failure is said in it.
  React.useEffect(() => {
    if (running === null || running.state === 'writing') return;
    dismissExport(running.id);
    setExportId(null);
    if (running.state === 'saved') onClose();
    else setSaid(running.said);
  }, [running, onClose]);

  // A dialog that goes while its export is out — its page left — hands it to the panel.
  const held = React.useRef(exportId);
  held.current = exportId;
  React.useEffect(
    () => () => {
      if (held.current !== null) letGoExport(held.current);
    },
    []
  );
  const letGo = () => {
    if (exportId !== null) letGoExport(exportId);
    setExportId(null);
    onClose();
  };
  const cancel = () => {
    if (exportId !== null) cancelExport(exportId);
    setExportId(null);
    onClose();
  };
  const writing = running?.state === 'writing';

  return (
    <Dialog open={open} onOpenChange={(next) => !next && letGo()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Export</DialogTitle>
          <DialogDescription>
            Every row this list matches
            {total !== null ? ` — ${total.toLocaleString('en-US')}` : ''}.
          </DialogDescription>
        </DialogHeader>
        <DialogBody
          as="form"
          className="text-sm"
          onSubmit={(event) => {
            event.preventDefault();
            if (!writing) download();
          }}
        >
          <fieldset className="grid gap-1.5" disabled={writing}>
            <legend className="mb-1 font-medium">Format</legend>
            {FORMATS.map((one) => (
              <label key={one.format} className="flex items-center gap-2">
                <input
                  type="radio"
                  name="export-format"
                  checked={format === one.format}
                  onChange={() => setFormat(one.format)}
                  className="accent-[var(--color-teal)]"
                />
                {one.label}
              </label>
            ))}
          </fieldset>
          <fieldset className="grid gap-1.5" disabled={writing}>
            <legend className="mb-1 font-medium">Columns</legend>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="export-columns"
                checked={presetId === null}
                onChange={() => setPresetId(null)}
                className="accent-[var(--color-teal)]"
              />
              As shown ({shownColumns} {shownColumns === 1 ? 'column' : 'columns'})
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="export-columns"
                  checked={presetId !== null}
                  disabled={layouts.length === 0}
                  onChange={() => setPresetId(layouts[0]?.id ?? null)}
                  className="accent-[var(--color-teal)]"
                />
                A layout:
              </label>
              <ChoiceSelect
                aria-label="Export layout"
                value={presetId ?? ''}
                disabled={writing || layouts.length === 0}
                onChange={(value) => setPresetId(value || null)}
                className="h-8 w-72 max-w-full"
                choices={[
                  { value: '', label: layoutsWord(presets.data, presets.error) },
                  ...layouts.map((preset) => ({
                    value: preset.id,
                    label: layoutName(preset, firmLabel)
                  }))
                ]}
              />
            </div>
            {presets.error && (
              <p role="alert" className="text-destructive">
                The export layouts could not be read: {presets.error}
              </p>
            )}
          </fieldset>
          <p className="text-muted-foreground">
            Internal ID is always the first column, whatever the layout — a file sent back is
            matched by it.
          </p>
          {running && writing && (
            <div data-export-progress="" className="grid gap-1.5">
              <ExportBar label="Writing the file" />
              <ExportLine export={running} className="text-muted-foreground tabular-nums" />
            </div>
          )}
          {said && (
            <p role="alert" className="text-destructive">
              {said}
            </p>
          )}
          <DialogFooter className="px-0 pb-0">
            <Button type="button" variant="outline" onClick={cancel}>
              Cancel
            </Button>
            {writing ? (
              // Its own key, so React never turns this element back into the submit button while
              // its click is still out; `detail` > 1 is a double-click's second press landing where
              // Download was.
              <Button
                key="background"
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  if (event.detail <= 1) letGo();
                }}
              >
                Continue in background
              </Button>
            ) : (
              <Button key="download" type="submit">
                Download
              </Button>
            )}
          </DialogFooter>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

function layoutsWord(read: Nullable<IExportPreset[]>, error: Nullable<string>): string {
  if (error) return 'Could not be read';
  if (read === null) return 'Reading the layouts…';
  return read.length === 0 ? 'No layout for this survey' : 'Choose a layout';
}

/** A layout as the select names it — its name, and its firm beside it. */
function layoutName(preset: IExportPreset, firmLabel: (code: string) => string): string {
  return preset.firm ? `${preset.name} — ${firmLabel(preset.firm)}` : preset.name;
}
